export interface RouteDefinition<RouteId extends string = string> {
  id: RouteId
  path: string
  title?: string
}

export type NavigationKind = 'initial' | 'push' | 'pop'

export interface RouteState<RouteId extends string = string> {
  id: RouteId
  pathname: string
  navigationKind: NavigationKind
}

export const TCHOMA_NAVIGATION_EVENT = 'tchoma:navigation'

export interface NavigationEventDetail<RouteId extends string = string> {
  previous: RouteState<RouteId>
  current: RouteState<RouteId>
}

export type InstallCapability = 'installed' | 'native-prompt' | 'manual' | 'unavailable'
export type InstallOutcome = 'idle' | 'prompting' | 'accepted' | 'dismissed' | 'failed'
export type InstallPromptResult = 'accepted' | 'dismissed' | 'unavailable' | 'failed'

export interface InstallState {
  capability: InstallCapability
  outcome: InstallOutcome
  error?: string
}

export type ServiceWorkerStatus =
  | 'disabled'
  | 'unsupported'
  | 'registering'
  | 'ready'
  | 'update-available'
  | 'error'

export interface ServiceWorkerState {
  status: ServiceWorkerStatus
  error?: string
}

export interface RuntimeDiagnostics {
  secureContext: boolean
  standalone: boolean
  serviceWorkerSupported: boolean
  online: boolean
}

export function normalizePathname(pathname: string): string {
  const pathOnly = pathname.split(/[?#]/u, 1)[0] || '/'
  const withLeadingSlash = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`
  const compact = withLeadingSlash.replace(/\/{2,}/gu, '/')
  return compact.length > 1 ? compact.replace(/\/+$/u, '') : compact
}

export function validateRoutes<RouteId extends string>(
  routes: readonly RouteDefinition<RouteId>[],
  fallbackRouteId: RouteId,
): void {
  if (routes.length === 0) throw new Error('Web runtime requires at least one route')

  const ids = new Set<RouteId>()
  const paths = new Set<string>()
  let hasFallback = false

  for (const route of routes) {
    const path = normalizePathname(route.path)
    if (ids.has(route.id)) throw new Error(`Duplicate route id: ${route.id}`)
    if (paths.has(path)) throw new Error(`Duplicate route path: ${path}`)
    ids.add(route.id)
    paths.add(path)
    if (route.id === fallbackRouteId) hasFallback = true
  }

  if (!hasFallback) throw new Error(`Fallback route is not declared: ${fallbackRouteId}`)
}

export function routeForId<RouteId extends string>(
  routes: readonly RouteDefinition<RouteId>[],
  routeId: RouteId,
): RouteDefinition<RouteId> {
  const route = routes.find((candidate) => candidate.id === routeId)
  if (!route) throw new Error(`Unknown route id: ${routeId}`)
  return route
}

export function matchRoute<RouteId extends string>(
  routes: readonly RouteDefinition<RouteId>[],
  fallbackRouteId: RouteId,
  pathname: string,
): RouteDefinition<RouteId> {
  const candidatePath = normalizePathname(pathname)
  let bestMatch: RouteDefinition<RouteId> | undefined
  let bestLength = -1

  for (const route of routes) {
    const routePath = normalizePathname(route.path)
    const matches = routePath === '/'
      ? candidatePath === '/'
      : candidatePath === routePath || candidatePath.startsWith(`${routePath}/`)

    if (matches && routePath.length > bestLength) {
      bestMatch = route
      bestLength = routePath.length
    }
  }

  return bestMatch ?? routeForId(routes, fallbackRouteId)
}

export function createRouteState<RouteId extends string>(
  routes: readonly RouteDefinition<RouteId>[],
  fallbackRouteId: RouteId,
  pathname: string,
  navigationKind: NavigationKind,
): RouteState<RouteId> {
  const route = matchRoute(routes, fallbackRouteId, pathname)
  return {
    id: route.id,
    pathname: normalizePathname(pathname),
    navigationKind,
  }
}

export function resolveInstallCapability(input: {
  secureContext: boolean
  standalone: boolean
  promptAvailable: boolean
}): InstallCapability {
  if (input.standalone) return 'installed'
  if (!input.secureContext) return 'unavailable'
  return input.promptAvailable ? 'native-prompt' : 'manual'
}

export function resolveInitialServiceWorkerStatus(input: {
  enabled: boolean
  supported: boolean
}): ServiceWorkerStatus {
  if (!input.enabled) return 'disabled'
  return input.supported ? 'registering' : 'unsupported'
}
